// EditProject.jsx
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import AdminNavbar from "../Layout/AdminNavbar";
import { FaTrash } from "react-icons/fa";
import API_BASE_URL from "../config";

/* ──────────────────────────────────────────────
   Custom Searchable Dropdown (from AdminManageTeams)
────────────────────────────────────────────── */
const CustomSelect = ({ value, onChange, options, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [coords, setCoords] = useState({});
    const triggerRef = useRef(null);
    const inputRef = useRef(null);

    const selected = options.find((o) => String(o.value) === String(value));

    const recalc = () => {
        if (!triggerRef.current) return;
        const r = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - r.bottom;
        const openUp = spaceBelow < 270 && r.top > 270;
        setCoords({
            left: r.left,
            width: r.width,
            ...(openUp ? { bottom: window.innerHeight - r.top + 5 } : { top: r.bottom + 5 }),
        });
    };

    const handleOpen = () => { recalc(); setOpen(true); };

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (
                triggerRef.current && !triggerRef.current.contains(e.target) &&
                !e.target.closest(".csel-panel")
            ) { setOpen(false); setSearch(""); }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    useEffect(() => {
        if (open && inputRef.current) setTimeout(() => inputRef.current?.focus(), 0);
    }, [open]);

    const filtered = options.filter((o) =>
        o.label.toLowerCase().includes(search.toLowerCase())
    );

    const handleSelect = (opt) => { onChange(opt.value); setOpen(false); setSearch(""); };

    const panel = open ? (
        <div className="csel-panel" style={{ position: "fixed", zIndex: 99999, ...coords }}>
            <div className="csel-search-wrap">
                <svg className="csel-search-icon" width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <circle cx="5.5" cy="5.5" r="4" stroke="#A8A29E" strokeWidth="1.4" />
                    <path d="M9 9l2.5 2.5" stroke="#A8A29E" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
                <input ref={inputRef} className="csel-search" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <ul className="csel-list">
                {filtered.length === 0 ? (
                    <li className="csel-empty">No results</li>
                ) : (
                    filtered.map((opt) => (
                        <li
                            key={opt.value}
                            className={`csel-option ${String(opt.value) === String(value) ? "csel-option--active" : ""}`}
                            onMouseDown={() => handleSelect(opt)}
                        >
                            <span className="csel-option-label">{opt.label}</span>
                            {String(opt.value) === String(value) && (
                                <svg className="csel-check" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                    <path d="M2 6.5l3.5 3.5 5.5-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                        </li>
                    ))
                )}
            </ul>
        </div>
    ) : null;

    return (
        <div className="csel-root">
            <button
                ref={triggerRef}
                type="button"
                className={`csel-trigger ${open ? "csel-trigger--open" : ""}`}
                onClick={() => (open ? (setOpen(false), setSearch("")) : handleOpen())}
            >
                <span className={`csel-trigger-label ${!selected ? "csel-placeholder" : ""}`}>
                    {selected ? selected.label : placeholder}
                </span>
                <svg className={`csel-chevron ${open ? "csel-chevron--up" : ""}`} width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            {createPortal(panel, document.body)}
        </div>
    );
};

/* ──────────────────────────────────────────────
   Collapsible Section
────────────────────────────────────────────── */
const Section = ({ title, children }) => {
    const [open, setOpen] = useState(true);
    return (
        <div className="mb-3 border">
            <div
                className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
                onClick={() => setOpen(!open)}
            >
                <span>{title}</span>
                <span>{open ? "▾" : "▸"}</span>
            </div>
            {open && <div className="row g-3 p-3">{children}</div>}
        </div>
    );
};

const STATUSES = ["Planning", "Active", "On Hold", "Completed", "Archived", "Cancelled"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const VISIBILITIES = ["Private", "Internal", "Public"];
const PROJECT_TYPES = ["Internal", "Client", "Research", "Maintenance", "Compliance", "Other"];
const INDUSTRIES = ["IT", "Construction", "Manufacturing", "Consulting", "Marketing", "Healthcare", "Finance", "Education", "Other"];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];
const ACCENT_COLORS = ["#1e3a5f", "#2d6a4f", "#6d3b7d", "#a0522d", "#b5451b", "#1a5276", "#117a65", "#145a32", "#4a235a", "#784212"];

/* ──────────────────────────────────────────────
   Main Component
────────────────────────────────────────────── */
export default function EditProject() {
    const { id } = useParams();
    const navigate = useNavigate();

    // Project state
    const [formData, setFormData] = useState({});
    const [customers, setCustomers] = useState([]);
    const [ownerEmployees, setOwnerEmployees] = useState([]);
    const [tagInput, setTagInput] = useState("");
    const [systemNotes, setSystemNotes] = useState([]);
    const [activeTab, setActiveTab] = useState("details");
    const [activeSubTab, setActiveSubTab] = useState("systemNotes");
    const [navbarColor, setNavbarColor] = useState("#080235");

    // Team state
    const [teamMembers, setTeamMembers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [teamFormData, setTeamFormData] = useState({ teamId: "", employeeId: "", reporterId: "", projectId: "" });
    const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });

    const token = localStorage.getItem("token");

    // Bootstrap loader
    useEffect(() => {
        const bootstrapLink = document.createElement("link");
        bootstrapLink.rel = "stylesheet";
        bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
        document.head.appendChild(bootstrapLink);
        const iconsLink = document.createElement("link");
        iconsLink.rel = "stylesheet";
        iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
        document.head.appendChild(iconsLink);
        return () => { bootstrapLink.remove(); iconsLink.remove(); };
    }, []);

    // Auth check
    useEffect(() => {
        const roles = JSON.parse(localStorage.getItem("roles") || "[]");
        if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
        if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); return; }
    }, [navigate, token]);

    // Navbar color
    useEffect(() => {
        const userId = localStorage.getItem("userId");
        if (!token || !userId) return;
        fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((d) => { if (d?.navbarColor) setNavbarColor(d.navbarColor); })
            .catch(() => { });
    }, [token]);

    // Load project
    useEffect(() => {
        if (!id) return;
        fetch(`${API_BASE_URL}/api/projects/${id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((data) => setFormData({ tags: [], ...data }))
            .catch(() => toast.error("Failed to load project."));
    }, [id, token]);

    // Load customers
    useEffect(() => {
        fetch(`${API_BASE_URL}/api/customers/all?status=Active&size=200`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((d) => setCustomers(Array.isArray(d) ? d : d.content || []))
            .catch(() => { });
    }, [token]);

    // Load owner employees summary
    useEffect(() => {
        fetch(`${API_BASE_URL}/api/employees/active/summary`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((d) => setOwnerEmployees(Array.isArray(d) ? d : []))
            .catch(() => { });
    }, [token]);

    useEffect(() => {

        if (activeTab !== "system" || !id) return;

        axios.get(
            `${API_BASE_URL}/api/system-notes?entityType=PROJECT&entityId=${id}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        )
            .then((res) => {
                setSystemNotes(res.data || []);
            })
            .catch(() => {
                toast.error("Failed to load system notes.");
            });

    }, [activeTab, id, token]);

    // Load team members & teams — only when team tab is first opened
    const [teamDataLoaded, setTeamDataLoaded] = useState(false);

    useEffect(() => {
        if (activeTab !== "team" || teamDataLoaded || !id) return;
        const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
        Promise.all([
            axios.get(`${API_BASE_URL}/api/manageteam`, axiosConfig),
            axios.get(`${API_BASE_URL}/api/teams`, axiosConfig),
        ])
            .then(([teamRes, teamsRes]) => {
                const all = Array.isArray(teamRes.data) ? teamRes.data : [];
                setTeamMembers(all);
                setTeams(teamsRes.data);
                setTeamDataLoaded(true);
            })
            .catch(() => toast.error("Failed to load team data."));
    }, [activeTab, teamDataLoaded, id, token]);

    // Load employees when add modal opens
    useEffect(() => {
        if (!showAddModal) return;
        axios.get(`${API_BASE_URL}/api/employees/active`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => setEmployees(res.data))
            .catch(() => toast.error("Failed to load employees."));
    }, [showAddModal, token]);

    /* ── Project handlers ── */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const addTag = (val) => {
        const t = val.trim();
        if (t && !(formData.tags || []).includes(t)) {
            setFormData((prev) => ({ ...prev, tags: [...(prev.tags || []), t] }));
        }
        setTagInput("");
    };

    const removeTag = (tag) => {
        setFormData((prev) => ({ ...prev, tags: (prev.tags || []).filter((t) => t !== tag) }));
    };

    const formatDateTime = (iso) => {
        if (!iso) return "";
        return new Date(iso).toLocaleString("en-IN", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name?.trim()) { toast.warning("Project name is required."); return; }
        if (!formData.key?.trim()) { toast.warning("Project key is required."); return; }
        if (formData.projectType === "Client" && !formData.customer) { toast.warning("Customer is required for Client projects."); return; }
        if (formData.endDate && formData.startDate && formData.endDate < formData.startDate) { toast.warning("End date must be after start date."); return; }
        try {
            const res = await fetch(`${API_BASE_URL}/api/projects/${id}`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            if (!res.ok) throw new Error(await res.text());
            toast.success("Project updated successfully!");
            navigate(`/viewproject/${id}`);
        } catch (err) {
            toast.error(err.message || "Failed to update project.");
        }
    };

    /* ── Team helpers ── */
    const initials = (first, last) => `${(first?.[0] || "").toUpperCase()}${(last?.[0] || "").toUpperCase()}`;

    const sortData = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
        setSortConfig({ key, direction });
    };
    const getVal = (m, key) => {
        switch (key) {
            case "employee": return m.employeeFirstName ?? "";
            case "team": return m.teamName ?? "";
            case "reporter": return m.reporterFirstName ?? "";
            default: return "";
        }
    };
    const arrow = (key) => sortConfig.key === key ? (sortConfig.direction === "asc" ? " ↑" : " ↓") : " ⇅";

    // Filter by project name (matches AdminManageTeams logic — API returns projectName, not projectId)
    const projectTeamMembers = formData.name
        ? teamMembers.filter((m) => m.projectName === formData.name)
        : [];

    let sortedMembers = [...projectTeamMembers];
    if (sortConfig.key) {
        sortedMembers.sort((a, b) => {
            const vA = getVal(a, sortConfig.key), vB = getVal(b, sortConfig.key);
            if (vA < vB) return sortConfig.direction === "asc" ? -1 : 1;
            if (vA > vB) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    }

    const handleAddMember = async (e) => {
        e.preventDefault();
        const { teamId, employeeId, reporterId } = teamFormData;
        if (!teamId || !employeeId || !reporterId) { toast.error("All fields are required."); return; }
        try {
            const payload = { ...teamFormData, projectId: id };
            const res = await axios.post(`${API_BASE_URL}/api/manageteam`, payload, {
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            });
            setTeamMembers((prev) => [...prev, res.data]);
            toast.success("Team member added!");
            setTeamFormData({ teamId: "", employeeId: "", reporterId: "", projectId: "" });
            setShowAddModal(false);
        } catch (err) {
            toast.error(err.response?.data || "Something went wrong.");
        }
    };

    const confirmDelete = async () => {
        if (!selectedMember) return;
        try {
            await axios.delete(`${API_BASE_URL}/api/manageteam/${selectedMember.assignmentId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setTeamMembers((prev) => prev.filter((m) => m.assignmentId !== selectedMember.assignmentId));
            toast.success(`${selectedMember.employeeFirstName} ${selectedMember.employeeLastName} removed.`);
            setShowDeleteModal(false);
            setSelectedMember(null);
        } catch {
            toast.error("Error removing member.");
        }
    };

    const teamOptions = [...teams].sort((a, b) => a.name.localeCompare(b.name)).map((t) => ({ value: t.id, label: t.name }));
    const employeeOptions = employees.map((e) => ({ value: e.id, label: `${e.firstName} ${e.lastName}` }));

    return (
        <>
            <div className="fixed-top"><AdminNavbar /></div>
            <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

                {/* ── Page Header ── */}
                <div className="mb-3">
                    <h4 className="m-0">
                        <i className="bi bi-kanban me-2"></i>
                        {formData.name ? formData.name : "Edit Project"}
                    </h4>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <div>
                            <button type="submit" form="editProjectForm" className="btn btn-primary me-2">Save</button>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Cancel</button>
                        </div>
                        <a href="/allprojects" className="text-primary text-decoration-none me-3">List</a>
                    </div>
                </div>

                <form id="editProjectForm" onSubmit={handleSubmit}>

                    {/* ── Basic Information ── */}
                    <Section title="Basic Information">
                        <div className="col-md-3">
                            <label className="form-label">Project Name <span className="text-danger">*</span></label>
                            <input type="text" name="name" className="form-control" value={formData.name || ""} onChange={handleChange} required />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Project Key <span className="text-danger">*</span></label>
                            <input
                                type="text" name="key" className="form-control"
                                value={formData.key || ""}
                                onChange={(e) => setFormData((p) => ({ ...p, key: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10) }))}
                                readOnly={formData.keyLocked}
                            />
                            {formData.keyLocked && <div className="form-text">Key is locked after first task is linked</div>}
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Icon (Emoji)</label>
                            <input type="text" name="icon" className="form-control" value={formData.icon || ""} onChange={handleChange} maxLength={2} placeholder="📁" />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Accent Color</label>
                            <div className="d-flex align-items-center gap-1 flex-wrap mt-1">
                                {ACCENT_COLORS.map((c) => (
                                    <div
                                        key={c}
                                        onClick={() => setFormData((p) => ({ ...p, accentColor: c }))}
                                        style={{
                                            width: 24, height: 24, borderRadius: 4, background: c,
                                            cursor: "pointer", border: formData.accentColor === c ? "2px solid #222" : "2px solid transparent",
                                        }}
                                    />
                                ))}
                                <input
                                    type="text" className="form-control form-control-sm" style={{ width: 90 }}
                                    value={formData.accentColor || "#1e3a5f"}
                                    onChange={(e) => setFormData((p) => ({ ...p, accentColor: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="col-md-12">
                            <label className="form-label">Description</label>
                            <textarea name="description" className="form-control" rows={3} value={formData.description || ""} onChange={handleChange} />
                        </div>
                    </Section>

                    {/* ── Classification ── */}
                    <Section title="Classification">
                        <div className="col-md-3">
                            <label className="form-label">Project Type <span className="text-danger">*</span></label>
                            <select name="projectType" className="form-select" value={formData.projectType || ""} onChange={handleChange}>
                                {PROJECT_TYPES.map((t) => <option key={t}>{t}</option>)}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Industry</label>
                            <select name="industry" className="form-select" value={formData.industry || ""} onChange={handleChange}>
                                <option value="">Select industry…</option>
                                {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Category</label>
                            <input type="text" name="category" className="form-control" value={formData.category || ""} onChange={handleChange} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">
                                Customer {formData.projectType === "Client" && <span className="text-danger">*</span>}
                            </label>
                            <select name="customer" className="form-select" value={formData.customer || ""} onChange={handleChange}>
                                <option value="">Select customer…</option>
                                {customers.map((c) => <option key={c.id} value={c.id}>{c.customerName}</option>)}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Owner</label>
                            <select name="owner" className="form-select" value={formData.owner || ""} onChange={handleChange}>
                                <option value="">Select owner…</option>
                                {ownerEmployees.map((e) => (
                                    <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Reference Number</label>
                            <input type="text" name="referenceNumber" className="form-control" value={formData.referenceNumber || ""} onChange={handleChange} />
                        </div>
                    </Section>

                </form>

                {/* ── Tabs ── */}
                <ul className="nav nav-tabs mb-2" style={{ backgroundColor: navbarColor }}>
                    {[
                        ["details", "Status & Timeline"],
                        ["budget", "Budget"],
                        ["tags", "Tags"],
                        ["team", `Manage Team ${projectTeamMembers.length > 0 ? `(${projectTeamMembers.length})` : ""}`],
                        ["system", "System Information"],
                    ].map(([tabId, label]) => (
                        <li className="nav-item" key={tabId}>
                            <button
                                type="button"
                                className={`nav-link ${activeTab === tabId ? "active" : ""}`}
                                onClick={() => setActiveTab(tabId)}
                                style={{
                                    color: activeTab === tabId ? navbarColor : "#fff",
                                    backgroundColor: activeTab === tabId ? "#fff" : "transparent",
                                    fontWeight: activeTab === tabId ? "600" : "normal",
                                }}
                            >
                                {label}
                            </button>
                        </li>
                    ))}
                </ul>

                <div className="border p-3">

                    {/* ── Status & Timeline ── */}
                    {activeTab === "details" && (
                        <>
                            <Section title="Status & Governance">
                                <div className="col-md-3">
                                    <label className="form-label">Status <span className="text-danger">*</span></label>
                                    <select name="status" className="form-select" value={formData.status || ""} onChange={handleChange}>
                                        {STATUSES.map((s) => <option key={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Priority</label>
                                    <select name="priority" className="form-select" value={formData.priority || ""} onChange={handleChange}>
                                        {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Visibility</label>
                                    <select name="visibility" className="form-select" value={formData.visibility || ""} onChange={handleChange}>
                                        {VISIBILITIES.map((v) => <option key={v}>{v}</option>)}
                                    </select>
                                </div>
                            </Section>
                            <Section title="Timeline">
                                <div className="col-md-3">
                                    <label className="form-label">Start Date</label>
                                    <input type="date" name="startDate" className="form-control" value={formData.startDate || ""} onChange={handleChange} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">End Date</label>
                                    <input type="date" name="endDate" className="form-control" value={formData.endDate || ""} min={formData.startDate || ""} onChange={handleChange} />
                                    <div className="form-text">Must be after start date</div>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Sprint Duration (days)</label>
                                    <input type="number" name="sprintDuration" className="form-control"
                                        value={formData.sprintDuration ?? 14} onChange={handleChange}
                                        min={1} max={90} />
                                    <div className="form-text">Auto-sprint length when sprint expires</div>
                                </div>
                            </Section>
                        </>
                    )}

                    {/* ── Budget ── */}
                    {activeTab === "budget" && (
                        <Section title="Budget">
                            <div className="col-md-3">
                                <label className="form-label">Estimated Budget</label>
                                <input type="number" name="estimatedBudget" className="form-control" value={formData.estimatedBudget || ""} onChange={handleChange} placeholder="0.00" step="0.01" min="0" />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Budget Currency</label>
                                <select name="budgetCurrency" className="form-select" value={formData.budgetCurrency || "INR"} onChange={handleChange}>
                                    {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                        </Section>
                    )}

                    {/* ── Tags ── */}
                    {activeTab === "tags" && (
                        <div className="p-2">
                            <label className="form-label fw-bold">Tags</label>
                            <div className="border rounded p-2 d-flex flex-wrap gap-2 align-items-center" style={{ minHeight: 42, background: "#fafaf9" }}>
                                {(formData.tags || []).map((t) => (
                                    <span key={t} className="badge" style={{ background: "#e8f0f8", color: "#1e3a5f", border: "1px solid #c8d8e8", fontSize: 12 }}>
                                        {t}
                                        <button type="button" className="btn-close ms-1" style={{ fontSize: 8 }} onClick={() => removeTag(t)} />
                                    </span>
                                ))}
                                <input
                                    type="text"
                                    className="border-0 flex-grow-1"
                                    style={{ outline: "none", minWidth: 100, background: "transparent", fontSize: 13 }}
                                    placeholder="Add tag, press Enter…"
                                    value={tagInput}
                                    onChange={(e) => setTagInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagInput); }
                                        if (e.key === "Backspace" && !tagInput && formData.tags?.length) {
                                            setFormData((p) => ({ ...p, tags: p.tags.slice(0, -1) }));
                                        }
                                    }}
                                    onBlur={() => tagInput.trim() && addTag(tagInput)}
                                />
                            </div>
                            <div className="form-text">Press Enter or comma to add tags</div>
                        </div>
                    )}

                    {/* ── Manage Team ── */}
                    {activeTab === "team" && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <h6 className="mb-0 fw-semibold">Team Members</h6>
                                    <small className="text-muted">{sortedMembers.length} member{sortedMembers.length !== 1 ? "s" : ""} assigned to this project</small>
                                </div>
                                <button className="btn btn-primary btn-sm" onClick={() => setShowAddModal(true)}>
                                    <i className="bi bi-person-plus me-1"></i> Add Member
                                </button>
                            </div>

                            {sortedMembers.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="bi bi-people fs-1 d-block mb-2"></i>
                                    <p className="mb-1 fw-semibold">No team members assigned</p>
                                    <small>Click "Add Member" to assign someone to this project.</small>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover table-sm align-middle">
                                        <thead>
                                            <tr>
                                                <th style={{ backgroundColor: "#DFE4EB" }}>Sr.no</th>
                                                {[
                                                    { key: "employee", label: "Employee" },
                                                    { key: "team", label: "Team" },
                                                    { key: "reporter", label: "Manager" },
                                                ].map(({ key, label }) => (
                                                    <th
                                                        key={key}
                                                        style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
                                                        onClick={() => sortData(key)}
                                                    >
                                                        {label}
                                                        <span className="ms-1 text-muted" style={{ fontSize: 11 }}>{arrow(key)}</span>
                                                    </th>
                                                ))}
                                                <th style={{ backgroundColor: "#DFE4EB" }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedMembers.map((m, idx) => (
                                                <tr key={m.assignmentId}>
                                                    <td className="text-muted" style={{ fontSize: 13 }}>{idx + 1}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div style={{
                                                                width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                                                                background: "#1e3a5f", color: "#fff",
                                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                                fontSize: 12, fontWeight: 600, overflow: "hidden",
                                                            }}>
                                                                {m.employeePhotoPath ? (
                                                                    <img src={`${API_BASE_URL}/uploads/${m.employeePhotoPath}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                                                ) : initials(m.employeeFirstName, m.employeeLastName)}
                                                            </div>
                                                            <span className="fw-medium">{m.employeeFirstName} {m.employeeLastName}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="badge" style={{ background: "#e8f0f8", color: "#1e3a5f", fontWeight: 500 }}>{m.teamName}</span>
                                                    </td>
                                                    <td>{m.reporterFirstName} {m.reporterLastName}</td>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            title="Remove member"
                                                            onClick={() => { setSelectedMember(m); setShowDeleteModal(true); }}
                                                        >
                                                            <FaTrash size={12} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── System Information ── */}
                    {activeTab === "system" && (
                        <div className="p-3 border rounded bg-white">
                            <div className="mb-3">
                                {/* <div className="form-check mb-2">
                                    <input
                                        type="checkbox" className="form-check-input" id="archivedCheck"
                                        checked={formData.status === "Archived"} readOnly disabled
                                    />
                                    <label className="form-check-label fw-bold ms-2" htmlFor="archivedCheck">ARCHIVED</label>
                                </div> */}
                                <div className="text-muted small">
                                    <strong>Date Created:</strong> {formatDateTime(formData.createdAt)}
                                </div>
                            </div>
                            <ul className="nav nav-tabs mb-3">
                                <li className="nav-item">
                                    <button className={`nav-link ${activeSubTab === "systemNotes" ? "active" : ""}`} onClick={() => setActiveSubTab("systemNotes")}>
                                        System Notes
                                    </button>
                                </li>
                            </ul>
                            {activeSubTab === "systemNotes" && (
                                <table className="table table-sm align-middle">
                                    <thead>
                                        <tr>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Date</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Set By</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Action</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Field</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Old Value</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>New Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {systemNotes.length === 0 ? (
                                            <tr><td colSpan="5" className="text-center text-muted">No records to show.</td></tr>
                                        ) : (
                                            systemNotes.map((note, i) => (
                                                <tr key={i}>
                                                    <td>{formatDateTime(note.changedAt)}</td>
                                                    <td>{note.changedBy}</td>
                                                    <td>
                                                        <span
                                                            className={`badge ${note.action === "CREATED"
                                                                    ? "bg-success"
                                                                    : note.action === "TEAM_MEMBER_ADDED"
                                                                        ? "bg-primary"
                                                                        : note.action === "TEAM_MEMBER_REMOVED"
                                                                            ? "bg-danger"
                                                                            : "bg-warning text-dark"
                                                                }`}
                                                        >
                                                            {note.action
                                                                ?.replaceAll("_", " ")
                                                                ?.toUpperCase()}
                                                        </span>
                                                    </td>
                                                    <td>{note.fieldName}</td>
                                                    <td>{note.oldValue || "—"}</td>
                                                    <td>{note.newValue || "—"}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Bottom Save/Cancel ── */}
                <div className="d-flex align-items-center mt-3 mb-4">
                    <button type="submit" form="editProjectForm" className="btn btn-primary me-2">Save</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Cancel</button>
                </div>
            </div>

            {/* ── Add Member Modal ── */}
            {showAddModal && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={() => setShowAddModal(false)} />
                    <div className="modal fade show d-block" style={{ zIndex: 1050 }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header" style={{ backgroundColor: "#f8f9fa" }}>
                                    <div>
                                        <h5 className="modal-title fw-semibold mb-0">Add Team Member</h5>
                                        <small className="text-muted">Assign a member to <strong>{formData.name}</strong></small>
                                    </div>
                                    <button type="button" className="btn-close" onClick={() => setShowAddModal(false)} />
                                </div>
                                <div className="modal-body">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-medium">Team <span className="text-danger">*</span></label>
                                            <CustomSelect
                                                value={teamFormData.teamId}
                                                onChange={(v) => setTeamFormData((prev) => ({ ...prev, teamId: v }))}
                                                options={teamOptions}
                                                placeholder="Select Team"
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-medium">Employee <span className="text-danger">*</span></label>
                                            <CustomSelect
                                                value={teamFormData.employeeId}
                                                onChange={(v) => setTeamFormData((prev) => ({ ...prev, employeeId: v }))}
                                                options={employeeOptions}
                                                placeholder="Select Employee"
                                            />
                                        </div>
                                        <div className="col-md-12">
                                            <label className="form-label fw-medium">Reporter / Manager <span className="text-danger">*</span></label>
                                            <CustomSelect
                                                value={teamFormData.reporterId}
                                                onChange={(v) => setTeamFormData((prev) => ({ ...prev, reporterId: v }))}
                                                options={employeeOptions}
                                                placeholder="Select Reporter"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-primary" onClick={handleAddMember}>Save</button>
                                    <button className="btn btn-light" onClick={() => setShowAddModal(false)}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ── Delete Confirm Modal ── */}
            {showDeleteModal && selectedMember && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={() => setShowDeleteModal(false)} />
                    <div className="modal fade show d-block" style={{ zIndex: 1050 }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered modal-sm">
                            <div className="modal-content">
                                <div className="modal-header border-0 pb-0">
                                    <div>
                                        <h5 className="modal-title fw-semibold text-danger mb-0">Remove Member</h5>
                                        <small className="text-muted">This action cannot be undone.</small>
                                    </div>
                                    <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
                                </div>
                                <div className="modal-body pt-2">
                                    <div className="d-flex align-items-center gap-3 p-3 rounded" style={{ background: "#f8f9fa" }}>
                                        <div style={{
                                            width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
                                            background: "#1e3a5f", color: "#fff",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            fontWeight: 600, fontSize: 14, overflow: "hidden",
                                        }}>
                                            {selectedMember.employeePhotoPath ? (
                                                <img src={`${API_BASE_URL}/uploads/${selectedMember.employeePhotoPath}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                            ) : initials(selectedMember.employeeFirstName, selectedMember.employeeLastName)}
                                        </div>
                                        <div>
                                            <p className="mb-0 fw-semibold">{selectedMember.employeeFirstName} {selectedMember.employeeLastName}</p>
                                            <small className="text-muted">{selectedMember.teamName} · Reports to: {selectedMember.reporterFirstName} {selectedMember.reporterLastName}</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-danger btn-sm" onClick={confirmDelete}>Remove</button>
                                    <button className="btn btn-light btn-sm" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}